import React from "react";
import Badge from "components/Badge";
import { ArrowsExpandIcon } from "@heroicons/react/solid";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import {
  CertificationOut,
  ComponentDetailsEPDGroupOUT,
  EPDConformityOut,
  InventoryEPDGroupOUT,
} from "api-client";
import Tooltip from "components/Tooltip";
import { isFailedStatus } from "utils";
import BPENotFoundIcon from "assets/images/icons/not-found.svg";
import { REFERENCE_UNITS } from "./consts";

export function getCurrentReferenceUnitValue(id?: string) {
  return REFERENCE_UNITS?.find((item) => item.id === id);
}

type BadgeTypes = "attention" | "success" | "warning" | "info" | "auto";

export const ProcessingStatus = ({
  status,
  mapped,
  total,
}: {
  status: string | undefined;
  mapped: number | undefined;
  total: number | undefined;
}) => {
  const { t } = useTranslation();
  let badgeType: BadgeTypes = "auto";
  let label = "";
  let tooltipMessage = "";

  const calcPercentage = () => (100 * (mapped ?? 0)) / (total ?? 1);
  const getValue = () =>
    (mapped || 0) > 0 && calcPercentage() < 1
      ? Math.ceil(calcPercentage())
      : Math.floor(calcPercentage());

  const percentage = (total || 0) > 0 ? getValue() : 0;

  if (status === "uploaded") {
    badgeType = "attention";
    label = t("buildingMaterialResources.processing");
    tooltipMessage = t("buildingMaterialResources.processingTooltip");
  } else if (status === "reports_ready") {
    if (mapped && total && mapped === total) {
      badgeType = "success";
      label = t("buildingMaterialResources.complete");
      tooltipMessage = t("buildingMaterialResources.completeTooltip");
    } else {
      badgeType = "attention";
      label = t("buildingMaterialResources.mappingStatus", {
        value: percentage,
      });
      tooltipMessage = t("buildingMaterialResources.mappingStatusTooltip");
    }
  } else if (isFailedStatus(status)) {
    badgeType = "warning";
    label = t("buildingMaterialResources.failedChipText");
    tooltipMessage = t("buildingMaterialResources.failedChipTooltip");
  }

  return (
    <div className="w-full flex">
      <Badge
        className="justify-between !whitespace-nowrap !px-4 min-w-fit"
        type={badgeType}
      >
        <div className="break-all">{label}</div>
        <Tooltip content={tooltipMessage} />
      </Badge>
    </div>
  );
};

type MQType = "Good" | "Okay" | "Poor" | "None";

export type Report = {
  imported: number | undefined;
  total: number | undefined;
  percentage: number;
};

interface MQProps {
  report: Report;
  onClick?: (report: Report) => void;
}

export const ModelQualityNoneChip = () => {
  const { t } = useTranslation();
  return (
    <div
      className={
        "bg-gray-100 relative max-w-[100px] h-[25px] rounded-md flex items-center justify-center font-semibold"
      }
    >
      {t("buildingMaterialResources.none")}
    </div>
  );
};

export const ModalQualityChip = ({ report, onClick }: MQProps) => {
  const { t } = useTranslation();

  const { percentage } = report;

  // Good progress and color green by default
  let color = "bg-green-100";
  let hoverColor = "group-hover:bg-green-200";
  let textColor = "text-green-800";
  let hoverTextColor = "group-hover:text-green-900";
  let quality: MQType = t("buildingMaterialResources.good");

  if (percentage <= 25) {
    color = "bg-red-100";
    hoverColor = "group-hover:bg-red-200";
    textColor = "text-red-800";
    hoverTextColor = "group-hover:text-red-900";
    quality = t("buildingMaterialResources.poor");
  } else if (percentage <= 50) {
    color = "bg-yellow-100";
    hoverColor = "group-hover:bg-yellow-200";
    textColor = "text-yellow-800";
    hoverTextColor = "group-hover:text-yellow-900";
    quality = t("buildingMaterialResources.okay");
  }

  function handleClick() {
    onClick && onClick(report);
  }

  return (
    <div
      onClick={handleClick}
      className={clsx(
        `bg-gray-100 relative max-w-[100px] h-[25px] rounded-md flex items-center group`,
        {
          "cursor-pointer": onClick,
        }
      )}
    >
      <div
        style={{
          width: `${percentage < 5 ? percentage + 2 : percentage}%`,
        }}
        className={`${color} z-1 absolute ${
          percentage === 100 ? "rounded-md" : "rounded-tl-md rounded-bl-md"
        } h-full`}
      >
        <div
          className={`${hoverColor} h-full ${
            percentage === 100 ? "rounded-md" : "rounded-tl-md rounded-bl-md"
          }`}
        />
      </div>
      <div className="z-1 flex justify-between px-2 items-center w-full">
        <p></p>
        <p className={`${textColor} font-semibold`}>{quality}</p>
        <ArrowsExpandIcon className={`w-4 ${textColor} ${hoverTextColor}`} />
      </div>
    </div>
  );
};

export function detailViewSkeleton() {
  return (
    <div className="animate-pulse">
      {[...Array(5).keys()].map((item) => (
        <div className="gap-4 mb-7" key={item}>
          <div className="h-4 bg-gray-200 rounded-lg dark:bg-gray-700 w-[40%] mb-4"></div>
          <div className="h-3 bg-gray-200 rounded-lg dark:bg-gray-700 w-full mb-4"></div>
          <div className="h-3 bg-gray-200 rounded-lg dark:bg-gray-700 w-full mb-4"></div>
          <div className="h-3 bg-gray-200 rounded-lg dark:bg-gray-700 w-full mb-4"></div>
        </div>
      ))}
    </div>
  );
}

export function EmptyStateOverview(props: { children: React.ReactNode }) {
  return (
    <div className="flex h-full -mt-20 text-center items-center justify-center text-sm text-gray-500">
      <div className="flex flex-col w-fit mt-16 items-center justify-center">
        <img alt="BPENotFoundIcon" src={BPENotFoundIcon} className="py-3" />
        <span className="font-normal text-sm text-gray-500 p-4 max-w-md text-left">
          {props.children}
        </span>
      </div>
    </div>
  );
}

export function complianceChipsGroup(
  allItems: CertificationOut[] | EPDConformityOut[],
  availableItems: string[] | undefined
) {
  return (
    <>
      {allItems?.map((rec) => {
        const itemExist =
          availableItems?.find((item) => item === rec.name) !== undefined;
        return (
          <div
            key={rec.id}
            className={clsx(
              "text-xs rounded-full h-[17px] px-2 mr-1 whitespace-nowrap font-medium flex items-center",
              itemExist
                ? "bg-green-100 text-green-700"
                : "bg-red-100 text-red-700"
            )}
          >
            {rec.name}
          </div>
        );
      })}
    </>
  );
}

export function createEpdsList(
  group: InventoryEPDGroupOUT | ComponentDetailsEPDGroupOUT
) {
  if (!group.epds || !group.epds.length) {
    return [];
  }
  return group.epds?.map((item) => {
    return {
      factor: item.factor,
      epd_name: item.epd?.name_de ?? "-",
      database: item.epd?.database ?? "-",
      dataset_source: item.epd?.dataset_source ?? "-",
      material_name: item.material ?? "-",
      dataset_url: item.epd?.url ?? undefined,
      recyclability_score: item?.material_recyclability_score ?? "-",
      recyclability_qualification:
        item?.material_recyclability_qualification ?? "-",
      reference_unit: item.epd?.reference_unit ?? "-",
      reference_size: item.epd?.reference_size ?? "-",
      conformity: group?.conformity ?? "-",
      mass_value_m: item.epd?.mass_value_m ?? "-",
      mass_value_m2: item.epd?.mass_value_m2 ?? "-",
      mass_value_m3: item.epd?.mass_value_m3 ?? "-",
    };
  });
}
